// ConversationPage.jsx

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { useTranslation } from "react-i18next";

import {
  LuMessageCircle,
  LuRefreshCcw,
} from "react-icons/lu";

import {
  getAllConversation,
  getConversation,
  sendMessage,
  deleteConversationForUser,
} from "../Services/messageService.jsx";

import ConversationList from "../components/ConversationList";
import MessageBox from "../components/MessageBox";
import Spinner from "../components/Spinner";

import {
  auth,
  db,
} from "../firebase";

const ConversationPage = () => {
  const { t } = useTranslation();

  const [
    currentUser,
    setCurrentUser,
  ] = useState(null);

  const [
    authReady,
    setAuthReady,
  ] = useState(false);

  const [
    allMessages,
    setAllMessages,
  ] = useState([]);

  const [
    selectedConversation,
    setSelectedConversation,
  ] = useState(null);

  const [
    messages,
    setMessages,
  ] = useState([]);

  const [
    newMessage,
    setNewMessage,
  ] = useState("");

  const [
    loading,
    setLoading,
  ] = useState(true);

  const [
    loadingConversation,
    setLoadingConversation,
  ] = useState(false);

  const [
    sending,
    setSending,
  ] = useState(false);

  const [
    error,
    setError,
  ] = useState("");

  const [
    recipientInfo,
    setRecipientInfo,
  ] = useState(null);

  // Conversation where the API refused another message (too_many_consecutive_messages).
  const [
    waitingConversationId,
    setWaitingConversationId,
  ] = useState(null);

  const [
    showConversationList,
    setShowConversationList,
  ] = useState(false);

  const messagesEndRef =
    useRef(null);

  const senderId =
    currentUser?.uid || null;

  // The API says the limit is already reached (waitingForReply, e.g. after a page reload):
  // the composer opens directly in « waiting for a reply ».
  const selectedConversationId =
    selectedConversation?.conversationId;
  const selectedWaiting =
    Boolean(selectedConversation?.waitingForReply);

  useEffect(() => {
    if (selectedConversationId && selectedWaiting) {
      setWaitingConversationId(selectedConversationId);
    }
  }, [selectedConversationId, selectedWaiting]);

  // As soon as the other person has answered, the wait is over (the flag of the list may be outdated).
  // Only the messages of the displayed conversation count (not those of the previous one while switching).
  const lastMessage =
    messages.length > 0
      ? messages[messages.length - 1]
      : null;
  const lastSenderId =
    lastMessage &&
    lastMessage.conversationId === selectedConversationId
      ? lastMessage.senderId
      : null;

  useEffect(() => {
    if (lastSenderId && senderId && lastSenderId !== senderId) {
      setWaitingConversationId((current) =>
        current === selectedConversationId ? null : current
      );
    }
  }, [lastSenderId, senderId, selectedConversationId]);

  /*
  |--------------------------------------------------------------------------
  | Auth Firebase
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    const unsubscribe =
      onAuthStateChanged(
        auth,
        (user) => {
          setCurrentUser(user || null);
          setAuthReady(true);
        }
      );

    return unsubscribe;
  }, []);

  /*
  |--------------------------------------------------------------------------
  | Charger toutes les conversations
  |--------------------------------------------------------------------------
  */

  const fetchAllMessages =
    useCallback(async () => {
      if (!senderId) {
        setAllMessages([]);
        setSelectedConversation(null);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError("");

      try {
        const response =
          await getAllConversation(
            senderId
          );

        const conversations =
          Array.isArray(response)
            ? response
            : [];

        setAllMessages(
          conversations
        );

        setSelectedConversation(
          (previousConversation) => {
            if (
              previousConversation
            ) {
              const existing =
                conversations.find(
                  (conversation) =>
                    conversation.conversationId ===
                    previousConversation.conversationId
                );

              if (existing) {
                return existing;
              }
            }

            return (
              conversations[0] ||
              null
            );
          }
        );
      } catch (requestError) {
        console.error(
          "Erreur récupération conversations :",
          requestError
        );

        setError(
          requestError?.message ||
            t(
              "messages.load_error",
              "Impossible de charger vos conversations."
            )
        );
      } finally {
        setLoading(false);
      }
    }, [senderId, t]);

  useEffect(() => {
    if (!authReady) {
      return;
    }

    fetchAllMessages();
  }, [
    authReady,
    fetchAllMessages,
  ]);

  /*
  |--------------------------------------------------------------------------
  | Charger conversation sélectionnée
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    let mounted = true;

    const fetchConversation =
      async () => {
        if (
          !selectedConversation
            ?.conversationId ||
          !senderId
        ) {
          setMessages([]);
          return;
        }

        setLoadingConversation(true);

        try {
          const response =
            await getConversation(
              selectedConversation
                .conversationId,
              senderId
            );

          if (!mounted) {
            return;
          }

          setMessages(
            Array.isArray(response)
              ? response
              : []
          );
        } catch (requestError) {
          if (!mounted) {
            return;
          }

          console.error(
            "Erreur récupération conversation :",
            requestError
          );

          setError(
            requestError?.message ||
              t(
                "messages.conversation_error",
                "Impossible de charger cette conversation."
              )
          );
        } finally {
          if (mounted) {
            setLoadingConversation(
              false
            );
          }
        }
      };

    fetchConversation();

    return () => {
      mounted = false;
    };
  }, [
    selectedConversation,
    senderId,
    t,
  ]);

  /*
  |--------------------------------------------------------------------------
  | Déterminer destinataire
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    const refresh = () => { if (senderId) fetchAllMessages(); };
    window.addEventListener('messagesResumed', refresh);
    window.addEventListener('online', refresh);
    return () => {
      window.removeEventListener('messagesResumed', refresh);
      window.removeEventListener('online', refresh);
    };
  }, [senderId, fetchAllMessages]);

  const recipientId =
    useMemo(() => {
      if (
        !selectedConversation ||
        !senderId
      ) {
        return null;
      }

      return senderId ===
        selectedConversation.senderId
        ? selectedConversation.receiverId
        : selectedConversation.senderId;
    }, [
      selectedConversation,
      senderId,
    ]);

  /*
  |--------------------------------------------------------------------------
  | Charger infos destinataire Firestore
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    let mounted = true;

    const fetchRecipientInfo =
      async () => {
        if (!recipientId) {
          setRecipientInfo(null);
          return;
        }

        try {
          const userDoc =
            await getDoc(
              doc(
                db,
                "users",
                recipientId
              )
            );

          if (!mounted) {
            return;
          }

          setRecipientInfo(
            userDoc.exists()
              ? userDoc.data()
              : null
          );
        } catch (requestError) {
          if (!mounted) {
            return;
          }

          console.error(
            "Erreur récupération destinataire :",
            requestError
          );

          setRecipientInfo(null);
        }
      };

    fetchRecipientInfo();

    return () => {
      mounted = false;
    };
  }, [recipientId]);

  /*
  |--------------------------------------------------------------------------
  | Événement global nouveau message
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    const handleNewMessageEvent =
      (event) => {
        const conversationId =
          event?.detail
            ?.conversationId;

        if (!conversationId) {
          return;
        }

        setAllMessages(
          (previousConversations) => {
            const conversation =
              previousConversations.find(
                (item) =>
                  item.conversationId ===
                  conversationId
              );

            if (!conversation) {
              /*
                Si la conversation n'est pas
                encore dans la liste, recharge.
              */

              fetchAllMessages();

              return previousConversations;
            }

            const others =
              previousConversations.filter(
                (item) =>
                  item.conversationId !==
                  conversationId
              );

            return [
              conversation,
              ...others,
            ];
          }
        );

        /*
          Si le message appartient à la conversation
          actuellement ouverte, recharge son contenu.
        */

        if (
          selectedConversation
            ?.conversationId ===
          conversationId
        ) {
          getConversation(
            conversationId,
            senderId
          )
            .then((response) => {
              setMessages(
                Array.isArray(response)
                  ? response
                  : []
              );
            })
            .catch((requestError) => {
              console.error(
                "Erreur actualisation conversation :",
                requestError
              );
            });
        }
      };

    window.addEventListener(
      "newMessage",
      handleNewMessageEvent
    );

    return () => {
      window.removeEventListener(
        "newMessage",
        handleNewMessageEvent
      );
    };
  }, [
    fetchAllMessages,
    selectedConversation,
    senderId,
  ]);

  /*
  |--------------------------------------------------------------------------
  | Scroll automatique
  |--------------------------------------------------------------------------
  */



  /*
  |--------------------------------------------------------------------------
  | Sélection d'une conversation
  |--------------------------------------------------------------------------
  */

  const handleSelectConversation =
    (conversation) => {
      setSelectedConversation(
        conversation
      );

      setShowConversationList(
        false
      );

      setError("");
    };

  /*
  |--------------------------------------------------------------------------
  | Envoyer un message
  |--------------------------------------------------------------------------
  */

  const handleSendMessage =
    async () => {
      if (
        sending ||
        !senderId ||
        !recipientId || selectedConversation?.propertyDetails?.isDeleted || selectedConversation?.propertyIsDeleted ||
        !selectedConversation
      ) {
        return;
      }

      const content =
        newMessage.trim();

      if (!content) {
        return;
      }

      const temporaryId = `temp-${Date.now()}`;

      const message = {
        id: temporaryId,
        conversationId: selectedConversation.conversationId,

        senderId,
        receiverId:
          recipientId,

        propertyId:
          selectedConversation
            .propertyId,

        content,

        timestamp:
          new Date().toISOString(),

        isRead: false,

        /*
          Important :
          ton backend distingue maintenant
          les biens classiques et les vacances.
        */

        isVacation:
          Boolean(
            selectedConversation
              .isVacation
          ),

        PropertyEmail:
          recipientInfo?.email ||
          "",

        name:
          recipientInfo?.name ||
          "",

        propertyName:
          currentUser
            ?.displayName ||
          "",

        adLabel:
          selectedConversation
            ?.propertyDetails
            ?.adLabel ||
          selectedConversation
            ?.propertyDetails
            ?.libelle ||
          "",

        tel:
          recipientInfo?.phone ||
          recipientInfo?.tel ||
          "",

        Adresse:
          selectedConversation
            ?.propertyDetails
            ?.adresse ||
          "",

        prix:
          selectedConversation
            ?.propertyDetails
            ?.price ??
          selectedConversation
            ?.propertyDetails
            ?.prix ??
          0,

        urlImages:
          selectedConversation
            ?.propertyDetails
            ?.urlImages ||
          "",

        adsUrl:
          window.location.href,
      };

      /*
        Envoi optimiste :
        on montre immédiatement le message.
      */

      setMessages(
        (previousMessages) => [
          ...previousMessages,
          message,
        ]
      );

      setNewMessage("");
      setSending(true);
      setError("");

      /*
        Remonter conversation en haut
      */

      setAllMessages(
        (previousConversations) => {
          const currentConversation =
            previousConversations.find(
              (conversation) =>
                conversation.conversationId ===
                selectedConversation
                  .conversationId
            ) ||
            selectedConversation;

          const others =
            previousConversations.filter(
              (conversation) =>
                conversation.conversationId !==
                selectedConversation
                  .conversationId
            );

          return [
            currentConversation,
            ...others,
          ];
        }
      );

      try {
        await sendMessage(
          message
        );

        /*
          Recharge après confirmation backend
          pour récupérer l'id/timestamp serveur.
        */

        const updatedMessages =
          await getConversation(
            selectedConversation
              .conversationId,
            senderId
          );

        setMessages(
          Array.isArray(
            updatedMessages
          )
            ? updatedMessages
            : []
        );
      } catch (requestError) {
        console.error(
          "Erreur envoi message :",
          requestError
        );

        /*
          Retirer le message temporaire
          si l'envoi échoue.
        */

        setMessages(
          (previousMessages) =>
            previousMessages.filter(
              (item) =>
                item.id !==
                temporaryId
            )
        );

        /*
          Remettre texte dans input
        */

        setNewMessage(content);

        // Messaging limit: the composer explains the wait instead of a generic error.
        if (requestError?.code === "too_many_consecutive_messages") {
          setWaitingConversationId(selectedConversation.conversationId);
          setError("");
          return;
        }

        setError(
          requestError?.message ||
            t(
              "messages.send_error",
              "Impossible d'envoyer le message."
            )
        );
      } finally {
        setSending(false);
      }
    };

  /*
  |--------------------------------------------------------------------------
  | Supprimer conversation
  |--------------------------------------------------------------------------
  */

  const handleDeleteConversationForUser =
    async (
      conversationId
    ) => {
      if (
        !conversationId ||
        !senderId
      ) {
        return;
      }

      const confirmed =
        window.confirm(
          t(
            "messages.delete_confirm",
            "Voulez-vous supprimer cette conversation ?"
          )
        );

      if (!confirmed) {
        return;
      }

      try {
        await deleteConversationForUser(
          conversationId,
          senderId
        );

        setAllMessages(
          (previousConversations) => {
            const updated =
              previousConversations.filter(
                (conversation) =>
                  conversation.conversationId !==
                  conversationId
              );

            /*
              Si conversation ouverte supprimée,
              sélectionne la première restante.
            */

            if (
              selectedConversation
                ?.conversationId ===
              conversationId
            ) {
              setSelectedConversation(
                updated[0] || null
              );

              setMessages([]);
            }

            return updated;
          }
        );
      } catch (requestError) {
        console.error(
          "Erreur suppression conversation :",
          requestError
        );

        setError(
          requestError?.message ||
            t(
              "messages.delete_error",
              "Impossible de supprimer cette conversation."
            )
        );
      }
    };

  /*
  |--------------------------------------------------------------------------
  | Chargement Auth
  |--------------------------------------------------------------------------
  */

  if (!authReady) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center bg-slate-50">
        <Spinner />
      </div>
    );
  }

  /*
  |--------------------------------------------------------------------------
  | Pas connecté
  |--------------------------------------------------------------------------
  */

  if (!currentUser) {
    return (
      <main className="flex min-h-[60vh] items-center justify-center bg-slate-50 px-4">
        <div className="max-w-md text-center">
          <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-green-50 text-green-600">
            <LuMessageCircle
              size={30}
            />
          </span>

          <h1 className="mt-5 text-xl font-black text-slate-900">
            {t(
              "messages.login_required",
              "Connexion requise"
            )}
          </h1>

          <p className="mt-2 text-sm leading-6 text-slate-500">
            {t(
              "messages.login_description",
              "Connectez-vous pour consulter vos conversations."
            )}
          </p>
        </div>
      </main>
    );
  }

  /*
  |--------------------------------------------------------------------------
  | Loading conversations
  |--------------------------------------------------------------------------
  */

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center bg-slate-50">
        <Spinner />
      </div>
    );
  }

  /*
  |--------------------------------------------------------------------------
  | Aucune conversation
  |--------------------------------------------------------------------------
  */

  if (
    allMessages.length === 0
  ) {
    return (
      <main className="min-h-[70vh] bg-slate-50 px-4 py-12 sm:px-6">
        <div className="mx-auto flex min-h-[480px] max-w-2xl items-center justify-center rounded-3xl border border-dashed border-slate-300 bg-white p-8 text-center shadow-sm">

          <div>
            <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-green-50 text-green-600">
              <LuMessageCircle
                size={30}
              />
            </span>

            <p className="mt-4 text-xs font-black uppercase tracking-[0.18em] text-green-600">
              Lina Immo
            </p>

            <h1 className="mt-2 text-2xl font-black text-slate-950">
              {t(
                "no_conversation",
                "Aucune conversation"
              )}
            </h1>

            <p className="mx-auto mt-3 max-w-md text-sm leading-6 text-slate-500">
              {t(
                "messages.empty_description",
                "Vos échanges avec les propriétaires et les utilisateurs apparaîtront ici."
              )}
            </p>

            <button
              type="button"
              onClick={
                fetchAllMessages
              }
              className="
                mt-6
                inline-flex
                h-11
                items-center
                justify-center
                gap-2
                rounded-xl
                border
                border-slate-200
                bg-white
                px-5
                text-sm
                font-bold
                text-slate-700
                transition
                hover:border-green-300
                hover:bg-green-50
                hover:text-green-700
              "
            >
              <LuRefreshCcw
                size={16}
              />

              {t(
                "refresh",
                "Actualiser"
              )}
            </button>
          </div>
        </div>
      </main>
    );
  }

  /*
  |--------------------------------------------------------------------------
  | Page
  |--------------------------------------------------------------------------
  */

  return (
    <main className="min-h-screen bg-slate-50 px-3 py-4 sm:px-4 sm:py-6 lg:px-6">
      <div className="mx-auto max-w-7xl">

        {/* Header */}

        <div className="mb-5 flex items-end justify-between gap-4">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.18em] text-green-600">
              Lina Immo
            </p>

            <h1 className="mt-1 text-2xl font-black text-slate-950 sm:text-3xl">
              {t(
                "messages.title",
                "Messages"
              )}
            </h1>
          </div>

          <button
            type="button"
            onClick={
              fetchAllMessages
            }
            disabled={loading}
            aria-label={t(
              "refresh",
              "Actualiser"
            )}
            className="
              flex
              h-10
              w-10
              items-center
              justify-center
              rounded-xl
              border
              border-slate-200
              bg-white
              text-slate-500
              transition
              hover:border-green-300
              hover:bg-green-50
              hover:text-green-700
              disabled:opacity-50
            "
          >
            <LuRefreshCcw
              size={17}
            />
          </button>
        </div>

        {/* Erreur */}

        {error && (
          <div className="mb-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-600">
            {error}
          </div>
        )}

        {/* Chat */}

        <div className={`messaging-shell ${showConversationList ? "show-list" : ""}`}>
          {/* Liste conversations */}

          <aside className="messaging-sidebar">
            <ConversationList
              allMessages={
                allMessages
              }

              selectedConversation={
                selectedConversation
              }

              /*
                Utilise notre fonction wrapper
                pour fermer le panneau mobile.
              */

              setSelectedConversation={
                handleSelectConversation
              }

              handleDeleteConversationForUser={
                handleDeleteConversationForUser
              }

              senderId={
                senderId
              }

              recipientInfo={
                recipientInfo
              }
            />
          </aside>

          {/* Messages */}

          <section className="messaging-thread">
            {loadingConversation ? (
              <div className="flex h-full min-h-[500px] items-center justify-center">
                <Spinner />
              </div>
            ) : selectedConversation ? (
              <MessageBox
              recipientId={recipientId}
                messages={
                  messages
                }

                setNewMessage={
                  setNewMessage
                }

                newMessage={
                  newMessage
                }

                handleSendMessage={
                  handleSendMessage
                }

                waitingForReply={
                  waitingConversationId ===
                    selectedConversation.conversationId &&
                  messages.length > 0 &&
                  messages[messages.length - 1]
                    .senderId === senderId
                }

                messagesEndRef={
                  messagesEndRef
                }

                selectedConversation={
                  selectedConversation
                }

                setShowConversationList={
                  setShowConversationList
                }

                showConversationList={
                  showConversationList
                }

                senderId={
                  senderId
                }

                allMessages={
                  allMessages
                }

                setSelectedConversation={
                  handleSelectConversation
                }

                handleDeleteConversationForUser={
                  handleDeleteConversationForUser
                }

                /*
                  Nouvelles props optionnelles.
                  MessageBox peut les ignorer
                  s'il ne les utilise pas encore.
                */

                recipientInfo={
                  recipientInfo
                }

                sending={
                  sending
                }
              />
            ) : (
              <div className="flex h-full min-h-[500px] items-center justify-center p-6 text-center">
                <div>
                  <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-green-50 text-green-600">
                    <LuMessageCircle
                      size={26}
                    />
                  </span>

                  <h2 className="mt-4 text-lg font-black text-slate-900">
                    {t(
                      "messages.select_conversation",
                      "Sélectionnez une conversation"
                    )}
                  </h2>

                  <p className="mt-2 text-sm text-slate-500">
                    {t(
                      "messages.select_description",
                      "Choisissez une conversation pour afficher les messages."
                    )}
                  </p>

                  <button
                    type="button"
                    onClick={() =>
                      setShowConversationList(
                        true
                      )
                    }
                    className="mt-5 inline-flex h-10 items-center justify-center rounded-xl bg-slate-950 px-4 text-sm font-bold text-white lg:hidden"
                  >
                    {t(
                      "messages.view_conversations",
                      "Voir les conversations"
                    )}
                  </button>
                </div>
              </div>
            )}
          </section>
        </div>
      </div>
    </main>
  );
};

export default ConversationPage;